import { unwrapResult } from '@reduxjs/toolkit';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Box, Text, Button } from '@rsos/components/capstone/base';
import Modal from '@rsos/components/capstone/base/Modal';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import {
  addAgency,
  removeDeclinedAgencyLinksTo,
  setPendingAgencyLinksTo,
} from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgenciesSlice';
import { notifyError } from '@rsos/utils/toastUtils';
import AgencyNameSelect from './AgencyNameSelect';
import FetchError from './FetchError/FetchError';
import IsFetching from './IsFetching/IsFetching';
import NoAgenciesAvailable from './NoAgenciesAvailable/NoAgenciesAvailable';
import { initialValues, validationSchema } from './utils';

// Modal name
const modalName = 'add-peer-agency-modal';

// Formik form name
const formName = 'addAgencyForm';

// Formik agency name field
const agencyName = 'agencyName';

/**
 * Pair an agency modal
 * @param {Array} agencies - List of availabe agencies that can be paired with.
 * @param {Object or String} fetchError - Fetch request error.
 * @param {Boolean} isFetching - Is the request being fetched?
 * @param {Boolean} isOpened - Is the modal open?
 * @param {Function} onClose - Modal onClose handler.
 * @param {Function} onHandleFetchRetry - Retry fetch available agencies handler.
 * @param {Boolean} showNoAgenciesAvailable - Should show the no agencies available component?
 */
const InviteAgencyModal = ({
  agencies,
  fetchError,
  isFetching,
  isOpened,
  onClose,
  onHandleFetchRetry,
  showNoAgenciesAvailable,
}) => {
  const dispatch = useDispatch();

  const { sinatra } = useSelector(state => state);

  const org = sinatra.user.currentOrg.name;

  const handleCloseModal = handleReset => {
    handleReset();
    onClose();
  };

  const handleAgencyNameChange = (selectedOption, setFieldValue) =>
    selectedOption
      ? setFieldValue(agencyName, selectedOption)
      : setFieldValue(agencyName, '');

  const handleFormSubmit = (values, resetForm) => {
    let data = {
      org,
      link_to: values[agencyName].id,
      link_type: 'agency_share_chat',
    };

    dispatch(addAgency(data))
      .then(unwrapResult)
      .then(response => {
        dispatch(removeDeclinedAgencyLinksTo(response.link_to.id));

        dispatch(setPendingAgencyLinksTo(response));

        resetForm();

        onClose();
      })
      .catch(error => {
        notifyError(error);
      });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => handleFormSubmit(values, resetForm)}
      validationSchema={validationSchema}
    >
      {({ handleReset, setFieldValue, values }) => (
        <Modal
          data-name={modalName}
          id={modalName}
          isOpened={isOpened}
          onClose={() => handleCloseModal(handleReset)}
          width={400}
        >
          <ModalHeader onClose={() => handleCloseModal(handleReset)}>
            <Box marginBottom={19}>
              <Text
                color="primaryText"
                data-name="modal-title"
                size="subheader"
              >
                Pair an agency
              </Text>
            </Box>
          </ModalHeader>
          <ModalBody>
            <Form id={formName}>
              <Box marginBottom={12}>
                {isFetching ? (
                  <IsFetching />
                ) : fetchError ? (
                  <FetchError
                    error={fetchError}
                    onHandleFetchRetry={onHandleFetchRetry}
                  />
                ) : showNoAgenciesAvailable ? (
                  <NoAgenciesAvailable />
                ) : (
                  <AgencyNameSelect
                    data={agencies}
                    label="Agency name"
                    onChange={selectedOption =>
                      handleAgencyNameChange(selectedOption, setFieldValue)
                    }
                    value={values[agencyName]}
                  />
                )}
              </Box>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              btnType="primary"
              dataName="invite-agency-button"
              disabled={!values?.[agencyName]}
              form={formName}
              grow
              noMargin
              size="large"
              type="submit"
            >
              Invite
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Formik>
  );
};

InviteAgencyModal.propTypes = {
  agencies: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      id: PropTypes.number,
    }),
  ),
  fetchError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isFetching: PropTypes.bool,
  isOpened: PropTypes.bool,
  onClose: PropTypes.func,
  onHandleFetchRetry: PropTypes.func,
  showNoAgenciesAvailable: PropTypes.bool,
};

export default InviteAgencyModal;
